import { useAtom, useAtomValue } from 'infrastructure/state/jotai.wrapper'
import React, { ChangeEvent, FunctionComponent, useMemo } from 'react'
import {
  CourseSummaryMapping,
  TreeParamKey,
} from '../../components/tree/summaries/types'
import { Box, Button, Checkbox } from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import { masterDetailsListAtom, sortedMastersAtom } from './atoms'
import { useMappingDataForMaster } from './utility'
import { useUrlSearchParam } from 'shared/hooks'

export function TreesTable() {
  const sorted = useAtomValue(sortedMastersAtom)
  const columns = useMemo(() => {
    return [
      {
        title: 'Select',
        width: 25,
        rowContent: ({ treeId }: CourseSummaryMapping) => {
          return <TreeSelectionCell masterId={treeId} />
        },
      },
      {
        title: 'Course',
        width: 50,
        rowContent: ({ curriculumTitle }: CourseSummaryMapping) =>
          curriculumTitle,
      },
      {
        title: 'SummaryCount',
        width: 50,
        rowContent: ({ summaryData }: CourseSummaryMapping) =>
          summaryData?.length,
      },
      {
        title: 'MissingMapping',
        width: 50,
        rowContent: ({ treeId }: CourseSummaryMapping) => (
          <MasterDetailsCell
            masterId={treeId}
            DetailsComponent={MissingMappings}
          />
        ),
      },
      {
        title: 'ToDoCount',
        width: 50,
        rowContent: ({ treeId }: CourseSummaryMapping) => (
          <MasterDetailsCell masterId={treeId} DetailsComponent={ToDoCount} />
        ),
      },
      {
        title: 'DoneCount',
        width: 50,
        rowContent: ({ treeId }: CourseSummaryMapping) => (
          <MasterDetailsCell masterId={treeId} DetailsComponent={DoneCount} />
        ),
      },
    ]
  }, [])

  return (
    <Box>
      <Table columns={columns} items={sorted} />
    </Box>
  )
}

function TreeSelectionCell({ masterId }: { masterId: string }) {
  const [currentSelected, setCurrentSelected] = useUrlSearchParam(TreeParamKey)
  const detailsList = useAtomValue(masterDetailsListAtom)
  const onChangeChecked = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setCurrentSelected(masterId)
    }
  }
  return (
    <Box>
      <Checkbox
        data-testid={`currSelect-${masterId}`}
        disabled={!detailsList.includes(masterId)}
        checked={masterId === currentSelected}
        onChange={onChangeChecked}
      />
    </Box>
  )
}

function MasterDetailsCell({
  masterId,
  DetailsComponent,
}: {
  masterId: string
  DetailsComponent: FunctionComponent<{ masterId: string }>
}) {
  const [detailsList, setDetailsList] = useAtom(masterDetailsListAtom)
  if (!detailsList.includes(masterId))
    return (
      <Button onClick={() => setDetailsList((prev) => [...prev, masterId])}>
        Check
      </Button>
    )
  return <DetailsComponent masterId={masterId} />
}

function MissingMappings({ masterId }: { masterId: string }) {
  const mappings = useMappingDataForMaster(masterId)
  if (!mappings) return <Box>Loading...</Box>
  return <Box>{mappings.unMapped.length}</Box>
}

function ToDoCount({ masterId }: { masterId: string }) {
  const mappings = useMappingDataForMaster(masterId)
  if (!mappings) return <Box>Loading...</Box>
  return <Box>{Object.values(mappings.mapped).length}</Box>
}

function DoneCount({ masterId }: { masterId: string }) {
  const mappings = useMappingDataForMaster(masterId)
  if (!mappings) return <Box>Loading...</Box>
  return <Box>{Object.values(mappings.done).length}</Box>
}
